/**
 * ErrorBoundary — catches uncaught render-time errors from any descendant
 * and renders a friendly recovery UI instead of letting React unmount the
 * tree (which surfaces as a "white screen" to users).
 *
 * Mounts at the top of `App.js` around `<Routes>`. Provides a "Reload" CTA
 * and (for non-prod builds) the error message + component stack so the
 * issue is debuggable without DevTools.
 */
import React from 'react';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Browsers + observability: a console.error is the standard hook
    // for any external error tracker we wire later (Sentry, LogRocket, etc.).
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info?.componentStack);
    this.setState({ info });
  }

  reset = () => this.setState({ error: null, info: null });

  reload = () => {
    try { window.location.reload(); } catch (_) { /* noop */ }
  };

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;
    const isDev = process.env.NODE_ENV !== 'production';

    return (
      <div data-testid="app-error-boundary" className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
        <div className="max-w-xl w-full bg-white border-2 border-rose-300 p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="text-rose-500 text-3xl leading-none">!</div>
            <div>
              <h1 className="text-xl font-black tracking-tight">Something broke on this screen.</h1>
              <p className="text-sm text-zinc-600 mt-1">
                We caught the error before it crashed the app. Reload to recover, or go back to the previous page —
                your data is safe (the action you tried might not have completed).
              </p>
            </div>
          </div>

          {isDev && error && (
            <pre className="text-[11px] bg-zinc-100 border border-zinc-300 p-3 overflow-auto max-h-64 font-mono whitespace-pre-wrap">
              {String(error?.message || error)}
              {info?.componentStack ? '\n\n' + info.componentStack : ''}
            </pre>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={this.reload}
              data-testid="error-boundary-reload"
              className="px-4 py-2 bg-zinc-900 text-white text-sm font-bold uppercase tracking-widest hover:bg-zinc-800"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.reset}
              data-testid="error-boundary-retry"
              className="px-4 py-2 border-2 border-zinc-900 text-zinc-900 text-sm font-bold uppercase tracking-widest hover:bg-zinc-100"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => { window.history.length > 1 ? window.history.back() : window.location.assign('/'); }}
              data-testid="error-boundary-back"
              className="px-4 py-2 border border-zinc-300 text-zinc-700 text-sm font-bold uppercase tracking-widest hover:bg-zinc-100"
            >
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }
}
